import { useRef, useState } from "react";
import { Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    playLabel: (title: string) => `Play video: ${title}`,
  },
  es: {
    playLabel: (title: string) => `Reproducir video: ${title}`,
  },
};

type VideoLightboxProps = {
  src: string;
  poster: string;
  title: string;
  caption?: string;
};

export function VideoLightbox({ src, poster, title, caption }: VideoLightboxProps) {
  const t = useCopy(COPY);
  const [open, setOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next && videoRef.current) {
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
        }
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={t.playLabel(title)}
          className="group relative block w-full overflow-hidden rounded-lg border border-white/10 bg-black/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
        >
          <img
            src={poster}
            alt={title}
            loading="lazy"
            decoding="async"
            className="block w-full h-auto aspect-video object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          />
          <span className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" aria-hidden="true" />
          <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
            <span className="flex h-16 w-16 md:h-20 md:w-20 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-black/30 transition-transform duration-300 group-hover:scale-110">
              <Play className="h-7 w-7 md:h-9 md:w-9 ml-1 fill-current" />
            </span>
          </span>
          {caption && (
            <span className="absolute bottom-3 left-4 right-4 text-left text-xs md:text-sm text-white/90 font-medium drop-shadow">
              {caption}
            </span>
          )}
        </button>
      </DialogTrigger>
      <DialogContent
        className="!max-w-5xl w-[95vw] !p-0 !gap-0 !border-0 !bg-black sm:rounded-lg overflow-hidden"
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          controls
          autoPlay
          playsInline
          preload="metadata"
          className="block w-full h-auto aspect-video bg-black"
        />
      </DialogContent>
    </Dialog>
  );
}
